import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity, Boxes, ShieldCheck, Flame, MessageCircle, Radio, Factory, PackageSearch, TrendingUp, UserCheck, Anchor, Users } from "lucide-react";
import api from "@/lib/api";

const nf = (n) => new Intl.NumberFormat("ro-RO").format(n || 0);
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const KIND_ICON = {
  listing: PackageSearch, request: PackageSearch, investor: TrendingUp,
  boiler: Flame, kiln: ShieldCheck, roi: TrendingUp, lead: MessageCircle, verified: Factory,
  expert_verify: UserCheck, escrow: Anchor,
};
const KIND_COLOR = {
  request: "text-orange-600", boiler: "text-rose-600", kiln: "text-emerald-600",
  roi: "text-indigo-600", lead: "text-emerald-600", verified: "text-slate-700",
  listing: "text-slate-700", investor: "text-indigo-600",
  expert_verify: "text-emerald-400", escrow: "text-indigo-400",
};

export default function MarketPulse({ mode = "full", embedded = false }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/market-stats").then((r) => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const id = setInterval(load, 45000); // live refresh
    return () => { alive = false; clearInterval(id); };
  }, []);

  const stats = [
    { icon: Boxes, key: "activeMachinery", val: data ? `${nf(data.active_machinery)}+` : "—", to: "/anunturi" },
    { icon: ShieldCheck, key: "verifiedCompanies", val: data ? nf(data.verified_companies) : "—", to: "/directory" },
    { icon: Flame, key: "dryingCapacity", val: data ? nf(data.drying_capacity_m3) : "—", to: "/anunturi?mk_primary=uscare-termice" },
    { icon: MessageCircle, key: "leadsToday", val: data ? nf(data.leads_today) : "—", to: "/cereri" },
  ];

  const feedText = (ev) => {
    let name = "", n = "";
    if (ev.kind === "escrow") { const parts = (ev.detail || "").split("|"); name = parts[0] || ""; n = parts[1] || "3"; }
    else if (ev.kind === "expert_verify") name = ev.detail || "";
    return t(`ux.market.f.${ev.kind}`, { county: ev.county || "România", detail: ev.detail || "", name, n });
  };

  const GuildInner = (data?.guild?.length ? (
    <div className="flex items-center gap-2.5 py-1.5 flex-wrap" data-testid="guild-strip">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400 inline-flex items-center gap-1.5 shrink-0"><Users className="w-3.5 h-3.5" /> {t("ux.market.guildTitle")}</span>
      <div className="flex items-center">
        {data.guild.slice(0, 7).map((g, i) => (
          <span key={i} title={g.name} data-testid={`guild-avatar-${i}`}
            className="relative w-6 h-6 -ml-1.5 first:ml-0 bg-slate-700 border border-slate-900 flex items-center justify-center text-[9px] font-black text-white overflow-hidden">
            {g.logo ? <img src={g.logo} alt="" className="w-full h-full object-cover" /> : initials(g.name)}
            {i < 3 && <span className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-green-400 border border-slate-900" />}
          </span>
        ))}
      </div>
    </div>
  ) : null);

  const StripInner = (
    <div className="flex items-center gap-x-5 gap-y-1 py-2 flex-wrap text-xs sm:text-sm" data-testid="market-stats">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-black text-amber-400 shrink-0">
        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" /></span>
        {t("ux.market.live")}
      </span>
      {stats.map((s) => (
        <Link key={s.key} to={s.to} className="group inline-flex items-center gap-1.5 whitespace-nowrap hover:text-white transition-colors" data-testid={`market-stat-${s.key}`}>
          <s.icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-orange-500 shrink-0 transition-colors" />
          <span className="font-black text-orange-500 tabular-nums group-hover:text-orange-400">{s.val}</span>
          <span className="text-slate-400 group-hover:text-slate-200 border-b border-dashed border-transparent group-hover:border-slate-500">{t(`ux.market.${s.key}`)}</span>
        </Link>
      ))}
    </div>
  );

  const FeedInner = (
    <div className="py-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <Radio className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">{t("ux.market.feedTitle")}</span>
      </div>
      <ul className="divide-y divide-slate-800/60" data-testid="market-feed">
        {(data?.feed || []).map((ev, i) => {
          const Icon = KIND_ICON[ev.kind] || Activity;
          return (
            <li key={i} className="flex items-center gap-2.5 px-1 py-1 text-xs" data-testid={`feed-item-${i}`}>
              <Icon className={`w-3.5 h-3.5 shrink-0 ${KIND_COLOR[ev.kind] || "text-slate-400"}`} />
              <span className="text-slate-200 truncate flex-1 min-w-0">{feedText(ev)}</span>
              <span className="text-slate-500 font-mono text-[10px] shrink-0 whitespace-nowrap">{t("ux.market.minsAgo", { n: ev.mins_ago })}</span>
            </li>
          );
        })}
        {!data && <li className="px-1 py-1 text-xs text-slate-500">…</li>}
      </ul>
    </div>
  );

  // Embedded (inside hero) — no dark wrapper, just the strip/feed.
  if (embedded) {
    return (
      <div className="text-white" data-testid="market-pulse-embedded">
        {mode !== "feed" && GuildInner}
        {mode !== "feed" && StripInner}
        {mode === "feed" && FeedInner}
      </div>
    );
  }

  return (
    <section className="bg-slate-950 text-white border-b border-slate-800" data-testid="market-pulse">
      {mode !== "feed" && (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6">{GuildInner}{StripInner}</div>
      )}
      {mode !== "strip" && (
        <div className="border-t border-slate-800/70 bg-slate-900/40">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-6">{FeedInner}</div>
        </div>
      )}
    </section>
  );
}
